export type DateOffsetUnit = "d" | "m" | "y";

export interface DateOffset {
  amount: number;
  unit: DateOffsetUnit;
}

export interface ParsedDateToken {
  /** The bare variable name (e.g. `current_date`). */
  name: string;
  /** Calculation applied to the base date, or null when none. */
  offset: DateOffset | null;
  /** moment format string, or null when the default should be used. */
  format: string | null;
}

/**
 * Default display format for a value-based (custom) date token with no
 * explicit `|format`. A bare `{{date_var}}` is substituted with the stored
 * value as-is on the published link (runtime resolution) and in the share
 * dialog, and dates are stored as `YYYY-MM-DD` — so the default must be that
 * same shape, or the popover/editor would promise one order and the link
 * render another. Built-in dates override it: use `defaultDateFormatFor`.
 */
export const DEFAULT_DATE_FORMAT = "YYYY-MM-DD";

// Built-in now-based variables have no stored value to stay consistent with,
// so they print a friendlier default: month/year just that part, the full
// date with a month name so it reads the same to US and international
// viewers (`07-10-2026` is July 10 to one, 7 October to the other). Mirrored
// in static-library `resolve-date.ts` and dotell-backend `sd-data-builder.js`.
// A Map, not an object literal: `{{constructor}}` must not hit the prototype.
const DEFAULT_FORMAT_BY_NAME = new Map<string, string>([
  ["current_date", "MMM D, YYYY"],
  ["current.date", "MMM D, YYYY"],
  ["today", "MMM D, YYYY"],
  ["current_month", "MMMM"],
  ["current_year", "YYYY"],
]);

/** Format a date token with no explicit `|format` prints. */
export const defaultDateFormatFor = (name: string): string =>
  DEFAULT_FORMAT_BY_NAME.get(name) ?? DEFAULT_DATE_FORMAT;

/** Format options shown in the popover's Format select. */
export const DATE_FORMAT_OPTIONS = [
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "MMMM D, YYYY",
  "MMM D, YYYY",
  "D MMM YYYY",
] as const;

/** Calculation units shown in the popover, mapped to the token suffix letter. */
export const DATE_UNIT_OPTIONS: { value: DateOffsetUnit; label: string }[] = [
  { value: "d", label: "days" },
  { value: "m", label: "months" },
  { value: "y", label: "years" },
];

// A signed integer amount + unit letter immediately before the optional
// `|format`, e.g. `+5d`, `-2m`. The name is whatever precedes it (lazy) so a
// bare `current_date` (no suffix) leaves name untouched.
const OFFSET_RE = /^(.*?)([+-]\d+)([dmy])$/;

/**
 * Parses a pill/token name of the shape `name[±Nd|m|y][|format]`. Every part
 * except the name is optional, so plain `{{current_date}}` parses cleanly.
 */
export const parseDateToken = (raw: string): ParsedDateToken => {
  let rest = raw;
  let format: string | null = null;

  const pipe = rest.indexOf("|");
  if (pipe !== -1) {
    format = rest.slice(pipe + 1) || null;
    rest = rest.slice(0, pipe);
  }

  let offset: DateOffset | null = null;
  const match = OFFSET_RE.exec(rest);
  if (match) {
    rest = match[1];
    offset = { amount: Number(match[2]), unit: match[3] as DateOffsetUnit };
  }

  return { name: rest, offset, format };
};

/**
 * Serializes back to the token name. Omits a zero offset and a null/default
 * format (the default for THIS name — see `defaultDateFormatFor`) so
 * unmodified date variables keep their bare `{{name}}` form, while an
 * explicit choice that differs from the name's default (e.g. ISO on
 * `current_date`) is kept.
 */
export const buildDateToken = ({
  name,
  offset,
  format,
}: ParsedDateToken): string => {
  let out = name;
  if (offset && offset.amount !== 0) {
    const sign = offset.amount > 0 ? "+" : "";
    out += `${sign}${offset.amount}${offset.unit}`;
  }
  if (format && format !== defaultDateFormatFor(name)) {
    out += `|${format}`;
  }
  return out;
};
